import { Component, Element, Event, EventEmitter, Fragment, h, Method, Prop, State, Watch } from '@stencil/core';

import type {
  PanenkaTouchEvent,
  Swiper,
  SwiperOptions,
  SwiperBreakpoints,
  AutoplayOptions,
  PaginationOptions,
} from '../../../services/interfaces';
import { SwiperService } from '../../../services/libraries';
import { parseProp } from '../../../services/utils';

@Component({
  tag: 'pk-carousel',
  styleUrl: './scss/pk-carousel.scss',
  scoped: true,
})
export class PkCarousel {
  // Own Properties
  // ====================

  private ctaContainerElem?: HTMLDivElement;
  private headerContainerElem?: HTMLDivElement;
  private prevControlsHighContrastValue: boolean;
  private staticFirstSlideElem?: HTMLDivElement;
  private swiper?: SwiperService;
  private swiperContainerElem?: HTMLDivElement;
  private swiperNextElem?: HTMLPkButtonElement;
  private swiperPrevElem?: HTMLPkButtonElement;
  private swiperWrapperElem?: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkCarouselElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() firstSlideFade: number = 1;
  @State() navigationTop?: string;
  @State() slideWidth?: number;

  // Public Property API
  // ========================

  /** If true slider wrapper will adapt its height to the height of the currently active slide **/
  @Prop({ reflect: true }) autoHeight?: boolean = false;

  /** When enabled Swiper will run in autoplay mode  **/
  @Prop({ reflect: true }) autoPlay?: AutoplayOptions | string;

  /** When enabled a margin and padding will be applied to the carousel container
   * to guarantee that slide items with box-shadow will be shown completely.
   * Please use it only if your slide items have box-shadow in their styles.
   */
  @Prop({ reflect: true }) boxShadowPadding?: boolean = false;

  /** Allows to set different parameter for different responsive breakpoints (screen sizes).
   *  Not all parameters can be changed in breakpoints, only those which are not required different layout and logic,
   *  like `slidesPerView`, `slidesPerGroup`, `spaceBetween`, `slidesPerColumn`.
   */
  @Prop({ reflect: true }) breakpoints?: SwiperBreakpoints | string;

  /** If true, the previous/next control buttons will be displayed as high-contrast buttons */
  @Prop({ reflect: true }) controlsHighContrast: boolean = false;

  /** Transition effect */
  @Prop({ reflect: true }) effect?: 'fade' | 'slide' = 'slide';

  /** Enable continuous loop mode  **/
  @Prop({ reflect: true }) loop?: boolean = false;

  /** Set to true if you also need to watch Mutations for Swiper parent elements */
  @Prop({ reflect: true }) observeParents: boolean = false;

  /**
   * Set to true to enable Mutation Observer on Swiper and its elements.
   * In this case Swiper will be updated (reinitialized) each time if you change its style
   * (like hide/show) or modify its child elements (like adding/removing slides)
   */
  @Prop({ reflect: true }) observer: boolean = false;

  /** Set to true if you also need to watch Mutations for Swiper slide children elements */
  @Prop({ reflect: true }) observeSlideChildren: boolean = false;

  /**
   * When enabled it will use ResizeObserver (if supported by browser) on swiper container to detect container resize
   * (instead of watching for window resize)
   */
  @Prop({ reflect: true }) resizeObserver: boolean = false;

  /** It will set the position of the navigation buttons (previous/next) */
  @Prop({ reflect: true }) navigationPosition: 'hidden' | 'aside' | 'default' | 'inside' = 'default';

  /** It will set the pagination format if enabled */
  @Prop({ reflect: true }) pagination?: PaginationOptions | string;

  /** If true, it will enable the parallax transition effect on swiper-slide elements. */
  @Prop({ reflect: true }) parallax?: boolean = false;

  /** Number of slides per view (slides visible at the same time on slider's container). */
  @Prop({ reflect: true }) slidesPerView?: number | 'auto' = 'auto';

  /** Duration of transition between slides (in ms)  **/
  @Prop({ reflect: true }) speed?: number = 300;

  /** If true, the navigation will be shown on mobile */
  @Prop({ reflect: true }) showMobileNavigation?: boolean = false;

  /** If true, the first slide will wrapped in a `static` slot container that won't slide. It will fade out/in instead. */
  @Prop({ mutable: true }) staticFirstSlide?: boolean = false;

  /** When enabled Swiper will be disabled and hide navigation buttons in case there are not enough slides for sliding. */
  @Prop({ reflect: true }) watchOverflow?: boolean = true;

  /** If true, the slide will scale up (will become slightly bigger) when is hovered */
  @Prop({ reflect: true }) scaleOnHover?: boolean = false;

  // Prop lifecycle events
  // =======================

  @Watch('autoHeight')
  @Watch('autoPlay')
  @Watch('breakpoints')
  @Watch('controlsHighContrast')
  @Watch('effect')
  @Watch('loop')
  @Watch('navigationPosition')
  @Watch('parallax')
  @Watch('pagination')
  @Watch('slidesPerView')
  @Watch('speed')
  @Watch('watchOverflow')
  @Watch('scaleOnHover')
  updateSwiperInstance() {
    this.setSwiper();
  }

  @Watch('navigationPosition')
  onNavigationPositionChange() {
    if (this.navigationPosition !== 'inside') {
      // If the navigation position during the same render,
      // make sure to set back the `controlsHighContrast` to the initial value
      this.controlsHighContrast = this.prevControlsHighContrastValue;
      return;
    }
    this.controlsHighContrast = true;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the `previous` navigation button is clicked */
  @Event() pkClickPrev: EventEmitter<{ swiper: Swiper; event: CustomEvent }>;

  /** Callback handler to be called when the `next` navigation button is clicked */
  @Event() pkClickNext: EventEmitter<{ swiper: Swiper; event: CustomEvent }>;

  /** Callback handler to be called when the carousel slides to the left */
  @Event() pkSlidePrev: EventEmitter<{ swiper: Swiper; event: PanenkaTouchEvent }>;

  /** Callback handler to be called when the carousel slides to the right */
  @Event() pkSlideNext: EventEmitter<{ swiper: Swiper; event: PanenkaTouchEvent }>;

  /** Callback handler to be called when the active slide changes */
  @Event() pkSlideChange: EventEmitter<Swiper>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.prevControlsHighContrastValue = this.controlsHighContrast;
    this.onNavigationPositionChange();
  }

  componentDidLoad() {
    this.onHeadingCtaSlotChange();
    this.setSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Get the initialized instance of the Swiper library used under the hook. */
  @Method()
  async getSwiperInstance(): Promise<Swiper> {
    return Promise.resolve(this.swiper?.instance);
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get swiperBreakpoints() {
    if (!this.breakpoints) {
      // If no breakpoint property provided, then set default breakpoint parameters
      return {
        0: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 12 },
        600: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 32 },
        960: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 32 },
        1440: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 32 },
      };
    }

    return parseProp(this.breakpoints);
  }

  private get swiperPagination() {
    if (!this.pagination) return false;
    // If no el property provided, then set default el selector
    return { el: '.pk-carousel__pagination', ...parseProp(this.pagination) };
  }

  private get swiperOptions(): SwiperOptions {
    return {
      autoHeight: this.autoHeight,
      autoplay: this.autoPlay ? parseProp(this.autoPlay) : false,
      breakpoints: this.swiperBreakpoints,
      effect: this.effect,
      loop: this.loop,
      navigation: {
        nextEl: this.swiperNextElem,
        prevEl: this.swiperPrevElem,
      },
      observeParents: this.observeParents,
      observer: this.observer,
      observeSlideChildren: this.observeSlideChildren,
      parallax: this.parallax,
      pagination: this.swiperPagination,
      resizeObserver: this.resizeObserver,
      slidesPerView: this.slidesPerView,
      speed: this.speed,
      watchOverflow: this.watchOverflow,
    };
  }

  private setSwiper = () => {
    if (!this.swiperContainerElem || !this.swiperWrapperElem) return;

    this.swiper ??= new SwiperService(this.swiperContainerElem, this.swiperWrapperElem);
    this.swiper.init(this.swiperOptions);

    this.swiper.onSetTranslate(this.handleSlideMove);
    this.swiper.onSlideChange(this.handleSlideChange);
    this.swiper.onTouchSlide(this.handleTouchSlide);
    this.swiper.onTransitionEnd(this.handleStaticFirstSlideZIndex);
    this.swiper.onResize(this.setFixedSliderWidth);

    this.setFixedSliderWidth();
  };

  private destroySwiper = () => {
    if (!this.swiper) return;

    this.swiper.destroy();
    this.swiper = null;
  };

  private setFixedSliderWidth = () => {
    // If `staticFirstSlide="true"`, we need to set a fixed width to the first slide so it fill the space available
    this.slideWidth = this.el.querySelectorAll('.pk-carousel--slide')[0]?.clientWidth;
  };

  private handleSlideMove = (_: Swiper, translate: number) => {
    if (!this.staticFirstSlideElem) return;

    // Change the z-index of the static slide to make sure it's always behind the sliding one
    this.staticFirstSlideElem.style.zIndex = '-1';
    // If `staticFirstSlide="true"`, fade out the first static slide when the user drag the 2nd slide to the left
    const opacity = (this.slideWidth + translate) / this.slideWidth;

    if (opacity <= 0) this.firstSlideFade = 0;
    else if (opacity >= 1) this.firstSlideFade = 1;
    else this.firstSlideFade = opacity;
  };

  private handleSlideChange = (swiper: Swiper) => {
    this.pkSlideChange.emit(swiper);
  };

  private handleTouchSlide = (swiper: Swiper & { swipeDirection: 'prev' | 'next' }, event: PanenkaTouchEvent) => {
    if (swiper.swipeDirection === 'prev') {
      this.pkSlidePrev.emit({ swiper, event });
    }
    if (swiper.swipeDirection === 'next') {
      this.pkSlideNext.emit({ swiper, event });
    }
  };

  private handleNavigationClick = (direction: 'prev' | 'next') => (event: CustomEvent) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    if (direction === 'prev') {
      this.pkClickPrev.emit({ swiper: this.swiper?.instance, event });
    }
    if (direction === 'next') {
      this.pkClickNext.emit({ swiper: this.swiper?.instance, event });
    }
  };

  private handleStaticFirstSlideZIndex = (swiper: Swiper) => {
    if (!this.staticFirstSlideElem) return;

    if (swiper.isBeginning) {
      this.staticFirstSlideElem.style.zIndex = '10';
      return;
    }
    // Change the z-index of the static slide to make sure it's always behind the sliding one
    this.staticFirstSlideElem.style.zIndex = '-1';
  };

  private onHeadingCtaSlotChange = () => {
    const hasHeading = this.headerContainerElem && this.headerContainerElem.children.length > 0;
    const hasCta = this.ctaContainerElem && this.ctaContainerElem.children.length > 0;

    /**
     * NOTE: find a proper way to handle vertical position related to the slide items height
     *
     * We want to make sure that the navigation arrows are vertically placed in the middle of the slide items
     * when `aside` and `inside` position values are set.
     */

    if (hasHeading && !hasCta) {
      this.navigationTop = `calc(50% + ${this.swiperPrevElem.clientHeight}px * 0.5)`;
    }

    if (!hasHeading && hasCta) {
      this.navigationTop = `calc(50% - ${this.ctaContainerElem.clientHeight}px * 0.5 - ${this.swiperPrevElem.clientHeight}px * 0.5)`;
    }
  };

  private onSlidesSlotChange = () => {
    this.swiper?.update();
  };

  private get removeHeadlineMarginBottom() {
    return !this.headerContainerElem?.querySelector('[slot="headline"]') && this.navigationPosition !== 'default';
  }

  private renderPrevNextButtons = () => (
    <Fragment>
      <pk-button
        class="pk-carousel--navigation__prev"
        appearance="icon"
        disabled
        type="button"
        highContrast={this.controlsHighContrast}
        ref={(el: HTMLPkButtonElement) => (this.swiperPrevElem = el)}
        part="navigation-prev"
        onPkClick={this.handleNavigationClick('prev')}
      >
        <pk-icon name="navigation-chevron-left" part="navigation-prev-icon"></pk-icon>
      </pk-button>
      <pk-button
        class="pk-carousel--navigation__next"
        appearance="icon"
        type="button"
        highContrast={this.controlsHighContrast}
        ref={(el: HTMLPkButtonElement) => (this.swiperNextElem = el)}
        part="navigation-next"
        onPkClick={this.handleNavigationClick('next')}
      >
        <pk-icon name="navigation-chevron-right" part="navigation-next-icon"></pk-icon>
      </pk-button>
    </Fragment>
  );

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div
        class={{
          'pk-carousel': true,
          [`navigation-${this.navigationPosition}`]: true,
          'scale-spacing': this.scaleOnHover,
          'navigation-mobile': this.showMobileNavigation,
        }}
        part="base"
      >
        <div
          class={{
            'pk-carousel--header flex': true,
            'mb-[--carousel--container-margin-top]': !this.removeHeadlineMarginBottom,
          }}
          part="header"
        >
          <div class="headline grow" part="headline" ref={(div: HTMLDivElement) => (this.headerContainerElem = div)}>
            <slot name="headline" onSlotchange={this.onHeadingCtaSlotChange} />
          </div>
          {['default', 'aside'].includes(this.navigationPosition) && (
            <div
              class={`pk-carousel--navigation flex items-center justify-end position-${this.navigationPosition}`}
              style={{
                top: this.navigationTop,
              }}
              part="navigation"
            >
              {this.renderPrevNextButtons()}
            </div>
          )}
        </div>
        <div
          class={{
            'pk-carousel--container swiper-container': true,
            [`navigation-${this.navigationPosition}`]: true,
            'box-shadow-padding': this.boxShadowPadding,
            'scale-slides scale-spacing': this.scaleOnHover,
          }}
          ref={(el: HTMLDivElement) => (this.swiperContainerElem = el)}
        >
          {!!this.swiperPagination && (
            <div class={this.swiperPagination.el?.toString().replace(/^\./, '')} part="pagination"></div>
          )}
          {this.staticFirstSlide && (
            // Fixed container for the first static slide that won't slide
            <div
              class={{
                'pk-carousel__static-item': true,
                'scale-enabled': this.scaleOnHover,
              }}
              style={{
                width: `${this.slideWidth}px`,
                '--first-slide--fade-out': this.firstSlideFade.toFixed(2),
              }}
              ref={(div) => (this.staticFirstSlideElem = div)}
              part="static-item"
            >
              <slot name="static"></slot>
            </div>
          )}
          <div
            class="pk-carousel--container__wrapper"
            ref={(el: HTMLDivElement) => (this.swiperWrapperElem = el)}
            part="slides"
          >
            {/* Dummy slide item used as a mask over the static first slide item,
                so it pushed the rest to the right properly */}
            {this.staticFirstSlide && <div class="pk-carousel--slide mask" part="static-slide"></div>}
            <slot onSlotchange={this.onSlidesSlotChange} />
          </div>
          {this.navigationPosition === 'inside' && this.renderPrevNextButtons()}
        </div>
        <div
          class="pk-carousel--cta empty:hidden"
          part="cta"
          ref={(div: HTMLDivElement) => (this.ctaContainerElem = div)}
        >
          <slot name="cta" onSlotchange={this.onHeadingCtaSlotChange} />
        </div>
      </div>
    );
  }
}
